import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { AssessmentService } from "../../../../_services/admin/assessmentService";
import "../../../Sector_Question_Manage/control.css";
import KpiCascadeFilter, {
  kpiMatchesCascadeSteps,
} from "./KpiTagFilter";
import { injectAssessmentChrome } from "./assessmentChrome";

/**
 * Searchable multi-select KPI picker (riu_admin variant). Styling matches
 * reporting_module modals: link_bal_next / link_bal_next_cancel buttons,
 * native form-control search input.
 */
const KpiPickerModal = ({ show, onClose, selectedIds = [], onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState([]);
  const [search, setSearch] = useState("");
  const [filterSteps, setFilterSteps] = useState([]);
  const [picked, setPicked] = useState(new Set(selectedIds));

  useEffect(() => {
    injectAssessmentChrome();
  }, []);

  useEffect(() => {
    if (!show) return;
    setPicked(new Set(selectedIds));
    setSearch("");
    setFilterSteps([]);
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await AssessmentService.listKpis();
      if (!cancelled) {
        const list = res?.data?.data ?? res?.data ?? [];
        setKpis(Array.isArray(list) ? list : []);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const activeSteps = (filterSteps || []).filter((s) => s.values?.size > 0);
    return kpis.filter((k) => {
      if (needle) {
        const inSearch =
          (k.title || "").toLowerCase().includes(needle) ||
          String(k.id || "").includes(needle) ||
          (k.moduleName || "").toLowerCase().includes(needle);
        if (!inSearch) return false;
      }
      if (activeSteps.length && !kpiMatchesCascadeSteps(k, activeSteps)) {
        return false;
      }
      return true;
    });
  }, [kpis, search, filterSteps]);

  const toggle = (id) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Emit every checked id, even when the picker's list doesn't have a row
   * for it (e.g. an FY filter dropped it). The caller's state already has
   * the full row and preserves it by id.
   */
  const handleConfirm = () => {
    const objs = Array.from(picked).map((id) => {
      const k = kpis.find((x) => x.id === id);
      return {
        id,
        title: k?.title || "",
        default_weightage: k?.default_weightage ?? null,
      };
    });
    onConfirm(objs);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Pick KPIs</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "65vh", background: "#fafbfc" }}>
        <input
          type="text"
          className="kpi-input"
          placeholder="Search KPIs by title, module or id..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 10 }}
        />
        <div
          style={{
            padding: "10px 12px",
            border: "1px solid var(--rule)",
            borderRadius: 10,
            background: "var(--surface)",
            marginBottom: 12,
          }}
        >
          <KpiCascadeFilter
            kpiList={kpis}
            filterSteps={filterSteps}
            onFilterStepsChange={setFilterSteps}
            lockedSteps={[]}
          />
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 4px 8px",
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            fontFamily: "var(--font-mono)",
          }}
        >
          <span>
            {filtered.length} of {kpis.length} shown
          </span>
          <span>
            <span style={{ color: "var(--ink-2)" }}>{picked.size}</span>{" "}
            selected
          </span>
        </div>
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "24px 12px",
              color: "var(--ink-4)",
              fontSize: 13,
            }}
          >
            Loading…
          </div>
        ) : (
          <div
            style={{
              background: "var(--surface)",
              border: "1px solid var(--rule)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            {filtered.map((k, idx) => {
              const isChecked = picked.has(k.id);
              return (
                <label
                  key={k.id}
                  htmlFor={`kpi-${k.id}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "10px 14px",
                    cursor: "pointer",
                    borderTop: idx === 0 ? "none" : "1px solid #f1f5f9",
                    background: isChecked ? "#f0f9ff" : "transparent",
                    fontSize: 13,
                    color: "var(--ink-2)",
                    fontWeight: isChecked ? 600 : 400,
                    margin: 0,
                  }}
                >
                  <input
                    type="checkbox"
                    className="kpi-checkbox"
                    id={`kpi-${k.id}`}
                    checked={isChecked}
                    onChange={() => toggle(k.id)}
                  />
                  <span>{k.title || "(untitled)"}</span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div
                style={{
                  textAlign: "center",
                  padding: "24px 12px",
                  color: "var(--ink-4)",
                  fontSize: 13,
                }}
              >
                No KPIs match.
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer style={{ background: "var(--surface)", gap: 8 }}>
        <button type="button" className="kpi-btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="kpi-btn-primary"
          onClick={handleConfirm}
        >
          Done ({picked.size})
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default KpiPickerModal;
